import React from 'react';
import { PlusIconPath } from './constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  data: any;
  onChange: (data: any) => void;
  serviceNames?: string[];
}

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const StarIconEmpty: React.FC<{ className?: string }> = ({ className = 'w-10 h-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
  </svg>
);

const StarPicker: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        className="p-0.5 transition-colors"
        title={`${star} star${star > 1 ? 's' : ''}`}
      >
        <svg
          className={`w-5 h-5 ${star <= value ? 'text-amber-400 fill-amber-400' : 'text-zinc-300'}`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          fill={star <= value ? 'currentColor' : 'none'}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
        </svg>
      </button>
    ))}
  </div>
);

const TestimonialsEditor: React.FC<Props> = ({ data, onChange, serviceNames = [] }) => {
  const form = data || { summary: {}, testimonials: [] };

  const emit = (newForm: any) => onChange(newForm);

  const updateTestimonial = (idx: number, field: string, value: any) => {
    const testimonials = [...form.testimonials];
    testimonials[idx] = { ...testimonials[idx], [field]: value };
    emit({ ...form, testimonials });
  };

  const addTestimonial = () => {
    const testimonials = [...(form.testimonials || []), {
      name: '',
      rating: 5,
      text: '',
      date: new Date().toISOString().slice(0, 10),
      service: '',
    }];
    emit({ ...form, testimonials });
  };

  const removeTestimonial = (idx: number) => {
    const testimonials = [...form.testimonials];
    testimonials.splice(idx, 1);
    emit({ ...form, testimonials });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-display font-semibold text-primary">Customer Reviews</h2>
          <p className="text-sm text-muted mt-1">{form.testimonials?.length || 0} testimonials (5-8 required)</p>
        </div>
        <button onClick={addTestimonial} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium hover:bg-accent/20 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
          </svg>
          Add Review
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Overall Ratings Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Average star rating</Label>
            <Input type="number" step="0.1" min="1" max="5" value={form.summary?.averageRating ?? ''}
              onChange={(e) => emit({ ...form, summary: { ...form.summary, averageRating: Number(e.target.value) } })}
              placeholder="4.9" />
            <p className="text-xs text-muted">1–5 scale</p>
          </div>
          <div className="space-y-1.5">
            <Label>Total number of reviews</Label>
            <Input type="number" value={form.summary?.totalReviews ?? ''}
              onChange={(e) => emit({ ...form, summary: { ...form.summary, totalReviews: Number(e.target.value) } })}
              placeholder="127" />
          </div>
          <div className="space-y-1.5">
            <Label>% of 5-star reviews</Label>
            <Input type="number" min="0" max="100" value={form.summary?.fiveStarPercentage ?? ''}
              onChange={(e) => emit({ ...form, summary: { ...form.summary, fiveStarPercentage: Number(e.target.value) } })}
              placeholder="94" />
            <p className="text-xs text-muted">0–100</p>
          </div>
        </CardContent>
      </Card>

      {(!form.testimonials || form.testimonials.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <StarIconEmpty className="w-10 h-10 text-zinc-300 mb-3" />
          <p className="text-sm text-muted mb-4">No testimonials yet</p>
          <button onClick={addTestimonial} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors">
            Add your first review
          </button>
        </div>
      )}

      {(form.testimonials || []).map((t: any, idx: number) => (
        <div
          key={idx}
          data-editor-key={`testimonials.${idx}`}
          tabIndex={-1}
          className="border border-zinc-200 rounded-lg p-4 space-y-3 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-primary">#{t.id || idx + 1} — {t.name || 'Unnamed'}</span>
              <StarPicker value={t.rating || 5} onChange={(v) => updateTestimonial(idx, 'rating', v)} />
            </div>
            <button
              onClick={() => removeTestimonial(idx)}
              className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
              title="Remove review"
            >
              <TrashIcon />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input value={t.name || ''} onChange={(e) => updateTestimonial(idx, 'name', e.target.value)} placeholder="Customer name" />
            {serviceNames.length > 0 ? (
              <Select value={t.service || undefined} onValueChange={(v) => updateTestimonial(idx, 'service', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Which service?" />
                </SelectTrigger>
                <SelectContent>
                  {serviceNames.map((sn) => (
                    <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input value={t.service || ''} onChange={(e) => updateTestimonial(idx, 'service', e.target.value)} placeholder="Which service?" />
            )}
            <Input value={t.location || ''} onChange={(e) => updateTestimonial(idx, 'location', e.target.value)} placeholder="City / neighborhood" />
            <Input type="date" value={t.date || ''} onChange={(e) => updateTestimonial(idx, 'date', e.target.value)} />
          </div>
          <Textarea rows={3} value={t.text || ''} onChange={(e) => updateTestimonial(idx, 'text', e.target.value)}
            placeholder="What the customer said (at least a couple of sentences)" />
        </div>
      ))}
    </div>
  );
};

export default TestimonialsEditor;
